import type { ReactNode } from 'react';
import { AlertTriangle, CheckCircle, Info, Loader2, XCircle, type LucideIcon } from 'lucide-react';
import { STATUS_PALETTE, type StatusTone } from './statusPalette';

/**
 * StatusPanel — оцветена кутия за ПОСТОЯННО състояние: иконка, заглавие, текст, действия.
 *
 * 🔴 Защо не `InlineError`/`InlineStatus`. Те са за КРАТКО съобщение след действие: един
 * ред, без заглавие, а `error`/`warning` носят `role="alert"`. Assertive регионът
 * ПРЕКЪСВА екранния четец — правилно за „записът не мина", дефект за „картата не е
 * попълнена", което родителят чува при всяко отваряне на таба. Тук ролята е винаги
 * `status`, при всеки вариант, включително `danger`.
 *
 * 📌 Цветовете идват от `statusPalette.ts` — същият източник като `InlineMessage`.
 */

const ICON: Record<StatusTone, LucideIcon> = {
  success: CheckCircle,
  info: Info,
  warning: AlertTriangle,
  danger: XCircle,
};

export interface StatusPanelProps {
  variant: StatusTone;
  /** Какво е състоянието — едно изречение, четено първо. */
  title: string;
  /** Незадължително обяснение: защо и какво следва. */
  children?: ReactNode;
  /** Незадължителни бутони (`.btn .btn-primary` / `.btn-secondary` / `LoadingButton`). */
  actions?: ReactNode;
  /** Чака се резултат: иконката става въртяща се, а регионът е `aria-busy`. */
  busy?: boolean;
  className?: string;
}

export default function StatusPanel({
  variant,
  title,
  children,
  actions,
  busy = false,
  className = '',
}: StatusPanelProps) {
  const palette = STATUS_PALETTE[variant];
  const Icon = busy ? Loader2 : ICON[variant];

  return (
    <div
      role="status"
      aria-busy={busy || undefined}
      className={`flex items-start gap-3 rounded-lg border p-4 ${palette.surface} ${className}`}
    >
      <Icon
        size={20}
        className={`mt-0.5 flex-shrink-0 ${palette.icon} ${busy ? 'animate-spin' : ''}`}
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1 space-y-1">
        <p className="text-sm font-semibold">{title}</p>
        {children && <div className="text-sm">{children}</div>}
        {actions && <div className="flex flex-wrap gap-2 pt-2">{actions}</div>}
      </div>
    </div>
  );
}
